<div class="flex flex-col gap-3 h-full">
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div class="w-full flex justify-between ">
            <app-search-bar placeholder="{{ 'Search by name' | textCase }}" [(ngModel)]="searchValue"
                (searchChange)="onSearch()" class="w-[40%]" />

            <app-button variant="outlined" severity="contrast" icon="pi pi-filter" iconPosition="left"
                (clicked)="toggleFilterPanel()" [label]="'Filters'" />
        </div>
    </div>
    <div class="flex-1 min-h-0 flex flex-col overflow-y-auto">
        <ng-container *ngIf="jobLoading">
            <div class="flex items-center justify-center py-12">
                <i class="pi pi-spin pi-spinner text-2xl text-gray-400"></i>
            </div>
        </ng-container>

        <ng-container *ngIf="!jobLoading && jobGroups.length === 0">
            <div class="flex flex-col items-center justify-center py-12 text-center">
                <span class="text-base font-medium text-gray-600">{{ 'No tasks found' | textCase }}</span>
            </div>
        </ng-container>

        <ng-container *ngIf="!jobLoading && jobGroups.length > 0">
            <p-accordion [multiple]="true" class="border-accordion">
                <p-accordion-panel *ngFor="let group of jobGroups" [value]="group.groupId">
                    <p-accordion-header>
                        <div class="flex items-center gap-2 min-w-0 flex-1 mr-2">
                            <span class="text-sm font-semibold text-gray-900 truncate">{{ group.groupLabel }}</span>
                            <span class="ml-1 text-xs text-gray-500 font-normal flex-shrink-0">({{ group.jobs.length
                                }})</span>
                            @if (group.groupType !== JOB_GROUP.OTHER) {
                            <app-icon type="external-link" source="lucide" size="sm"
                                class="text-gray-900 hover:text-primary flex-shrink-0 cursor-pointer"
                                [pTooltip]="group.groupType === JOB_GROUP.GEOTECHNICAL_ANALYSIS ? 'Open analysis' : 'Open document'"
                                tooltipPosition="top" (click)="navigateToGroup($event, group)" />
                            }
                        </div>
                    </p-accordion-header>
                    <p-accordion-content>
                        <div class="divide-y divide-gray-100">
                            <div *ngFor="let job of group.jobs"
                                class="flex items-center justify-between gap-3 px-2 py-3 hover:bg-gray-50 transition cursor-pointer rounded"
                                (click)="jobAction(job)">
                                <!-- Status icon + display name -->
                                <div class="flex items-center gap-3 min-w-0 flex-1">
                                    @if (job.status === JOB_STATUS.STARTED) {
                                    <div class="flex items-center justify-center w-5 h-5 flex-shrink-0"
                                        (click)="$event.stopPropagation()">
                                        <img src="icons/template-library/generation_in_progress.svg" alt="Generating"
                                            class="w-4 h-4 animate-spin">
                                    </div>
                                    } @else {
                                    <app-icon [type]="getStatusIcon(job)" source="lucide" size="sm"
                                        [variant]="getStatusIconVariant(job)" class="flex-shrink-0" />
                                    }
                                    <span class="text-sm font-medium text-gray-800 truncate"
                                        [pTooltip]="getJobDisplayName(job)" tooltipPosition="top">{{
                                        getJobDisplayName(job) }}</span>
                                </div>

                                <!-- Created at + cancel -->
                                <div class="flex items-center gap-3 flex-shrink-0" (click)="$event.stopPropagation()">
                                    @if(job.createdAt) {
                                    <div class="flex items-center gap-1 text-xs text-gray-500">
                                        <i class="pi pi-sync icon-xs"></i>
                                        <span>Created at </span>
                                        <span class="font-medium text-gray-900">{{ job.createdAt | dateFormatter: 'Do
                                            MMMM, YYYY' }}</span>
                                        <span>by </span>
                                        <span class="font-medium text-gray-900">{{ job.user | fullName }}</span>
                                    </div>
                                    }
                                    @if (allowCancel(job)) {
                                    <app-popover #menuPopover [width]="'12rem'">
                                        <ng-template #trigger>
                                            <app-button variant="ghost" size="sm" icon="ellipsis-vertical"
                                                iconSource="lucide" class="p-1" />
                                        </ng-template>
                                        <ng-template #body>
                                            <app-button variant="ghost" severity="danger" label="Cancel task" icon="ban"
                                                iconSource="lucide" class="w-full justify-start"
                                                (clicked)="onCancel(job, menuPopover)" />
                                        </ng-template>
                                    </app-popover>
                                    }
                                </div>
                            </div>
                        </div>
                    </p-accordion-content>
                </p-accordion-panel>
            </p-accordion>

            <!-- Paginator -->
            <div class="my-2">
                <p-paginator [rows]="rowsPerPage" [totalRecords]="tasksTotalRecords" [first]="paginationFirst"
                    [rowsPerPageOptions]="[5, 10, 20, 50]"
                    (onPageChange)="onPageChange({ first: $event.first || 0, rows: $event.rows || 0 })" />
            </div>
        </ng-container>
    </div>
</div>

<p-drawer [(visible)]="filterPanelVisible" position="right" class="w-[22rem]" appendTo="body">
    <ng-template pTemplate="headless">
        <div class="flex flex-col h-full">
            <div class="flex items-center justify-between border-b p-4">
                <h2 class="text-lg font-semibold">Filter</h2>
                <app-button icon="pi pi-times" (clicked)="toggleFilterPanel()" variant="filled" severity="contrast" />
            </div>
            <div class="tasks-filter-fields flex-1 overflow-y-auto space-y-5 text-sm text-gray-800 p-4">
                <div>
                    <label class="block font-medium mb-1">Job Type</label>
                    <app-select [(value)]="selectedJobType" [options]="jobTypeOptions" [config]="jobTypeSelectConfig"
                        (onChange)="handleChange($event)" />
                </div>
                <div>
                    <label class="block font-medium mb-1">Status</label>
                    <app-select [(value)]="selectedStatus" [options]="statusOptions" [config]="statusSelectConfig"
                        (onChange)="handleStatusChange($event)" />
                </div>
                <div>
                    <label class="block font-medium mb-1">Created Date</label>
                    <app-datepicker [(value)]="filterDateRange" [config]="datePickerConfig" class="w-full" />
                </div>
            </div>
            <div class="flex justify-between items-center px-6 py-4 border-t">
                <app-button label="Clear all and apply" (clicked)="clearAllFilters()" [enableThrottle]="true"
                    variant="outlined" severity="contrast" />
                <app-button label="Apply Filter" (clicked)="applyFilters()" [enableThrottle]="true" variant="filled"
                    severity="primary" />
            </div>
        </div>
    </ng-template>
</p-drawer>